import { useState, useEffect, useMemo } from "react";
import { Button } from "@mui/material";
import SchoolIcon from "@mui/icons-material/School";
import BuildIcon from "@mui/icons-material/Build";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CoPresentIcon from "@mui/icons-material/CoPresent";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudIcon from "@mui/icons-material/Cloud";
import { useNavigate } from "react-router-dom";
import Translate from "../../utils/Translate";
import { formatDate } from "../../utils/dateTimeFormat";
import { hasAccess, accessKeys, hasSomeAccess } from "../../utils/userAccess";
import { getLicenseRegistrationCourseTypes } from "../../utils/externalUserHelper";
import DotMenu from "../DotMenu";
import YesOrNoDialog from "../YesOrNoDialog";
import { useApi } from "../../utils/Api";
import ActorLicensePrintDialog from "../actor-license-print/ActorLicensePrintDialog";
import { CustomNoRowsOverlay } from "../../utils/StyledGridOverlay";
import LicensCard from "./LicensCard";
import { Edit, Email, Output } from "@mui/icons-material";
import DigitalLicenseColumn from "./DigitalLicenseColumn";
import DigitalLicenseToContactDialog from "../booking/DigitalLicensesToContactDialog";
import EditDigitalLicenseDialogBase from "./EditDigitalLicenseDialogBase";
import { useSnackbar } from "notistack";
import DigitalLicenseToStudentDialog from "../booking/DigitalLicensesToStudentDialog";
import { useMediaQuery, useTheme } from "@mui/material";
import { DataGridPremium } from "@mui/x-data-grid-premium";
import { IconAndLabelColumnHeader } from "../../utils/dataGrid";
import { digitalLicenseStatus } from "../../utils/actorLicense";
import Personnummer from "personnummer";
import ExportBrokerDataDialog from "../actor-permits/broker/ExportBrokerDataDialog";

const getDotMenuActions = ({
  license,
  actorOrgNo,
  actorEmail,
  onDeleteLicense,
  onEditDigitalLicense,
  onResendArchiveRequest,
}) => {
  const hasCourseAdmin = hasAccess(accessKeys.courseAdmin);
  const hasStandardOrAdvanced = hasSomeAccess(accessKeys.standardPackage, accessKeys.advancedPackage);
  const hasFreeAndDigLic = hasAccess(accessKeys.freePackage) && hasAccess(accessKeys.digitalLicenses);
  const showMenuActions = hasCourseAdmin && (hasStandardOrAdvanced || hasFreeAndDigLic);
  const deleteLicenseDisabled = !license.hasArchiveRequest
    ? false
    : !license.archiveRequestMessageSendDate
      ? true
      : !license.archiveRequestMessageBounce;

  const resendArchiveRequestEnabled =
    license.hasArchiveRequest && license.archiveRequestMessageSendDate && !license.archiveRequestHasResponse;

  const hasValidPersonalNumber = Personnummer.valid(actorOrgNo, { allowCoordinationNumber: false });

  const oneMonthAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
  const menudata = showMenuActions
    ? [
        {
          icon: <Email />,
          text: Translate.get("SendDigitalLicenseToContact"),
          dialogHeader: Translate.get("SendDigitalLicenseToContact"),
          route: false,
          component: (
            <DigitalLicenseToContactDialog
              digitalLicenseId={license.digitalLicenseId}
              // defaultName={props.courseBookings[0].contactActorName}
              // defaultEmail={props.courseBookings[0].contactActorEmail}
            />
          ),
          hide: !hasAccess(accessKeys.digitalLicenses) || !license.digitalLicenseId || !hasValidPersonalNumber,
          disabled: license.hasArchiveRequest || license.digitalLicenseStatus != digitalLicenseStatus.published,
          customClass: "smallCourseDialog",
        },
        {
          icon: <Email />,
          text: hasValidPersonalNumber
            ? Translate.get("SendDigitalLicenseToStudent")
            : Translate.get("SendDigitalLicensePdfToStudent"),
          dialogHeader: hasValidPersonalNumber
            ? Translate.get("SendDigitalLicenseToStudent")
            : Translate.get("SendDigitalLicensePdfToStudent"),
          route: false,
          component: (
            <DigitalLicenseToStudentDialog
              digitalLicenseId={license.digitalLicenseId}
              sendAsPdf={!hasValidPersonalNumber}
            />
          ),
          hide: !hasAccess(accessKeys.digitalLicenses) || !license.digitalLicenseId,
          disabled:
            license.hasArchiveRequest ||
            license.digitalLicenseStatus != digitalLicenseStatus.published ||
            (!hasValidPersonalNumber && (actorEmail === "" || actorEmail == null)),
          customClass: "smallCourseDialog",
        },
        {
          icon: <Output />,
          text: Translate.get("ExportBrokerDataTitle"),
          component: <ExportBrokerDataDialog actorLicenseIds={[license.actorLicenseId]} />,
          disabled: !license.digitalLicenseId || license.digitalLicenseStatus != digitalLicenseStatus.published,
          hide: !hasAccess(accessKeys.actorPermitBrokerSource) || !hasAccess(accessKeys.digitalLicenses),
          customClass: "bigCourseDialog",
        },
        {
          icon: <Edit />,
          text: Translate.get("EditDigitalLicense"),
          route: false,
          hide: !hasAccess(accessKeys.digitalLicenses) || !license.digitalLicenseId,
          disabled: license.hasArchiveRequest || !license.regDate || new Date(license.regDate) < oneMonthAgo,
          onClick: onEditDigitalLicense,
        },
        ...(resendArchiveRequestEnabled
          ? [
              {
                icon: <DeleteIcon />,
                text: Translate.get("ResendDeleteRequest"),
                onClick: onResendArchiveRequest,
              },
            ]
          : [
              {
                icon: <DeleteIcon />,
                text: Translate.get("DeleteActorLicense"),
                onClick: onDeleteLicense,
                disabled: deleteLicenseDisabled,
              },
            ]),
      ]
    : [];
  return menudata;
};

const Comp = ({ rows, smallScreen, onOrderLicenseSuccess, hideFilter = true, actorOrgNo, actorEmail }) => {
  const [printActorLicenseId, setPrintActorLicenseId] = useState(null);
  const [editDigitalLicense, setEditDigitalLicense] = useState(null);
  const [licenseRegistrationCourseTypes, setLicenseRegistrationCourseTypes] = useState([]);
  const [deleteDetails, setDeleteDetails] = useState(null);
  const [resendArchiveRequestForDigitalLicenseId, setResendArchiveRequestForDigitalLicenseId] = useState(null);
  const api = useApi();
  const navigate = useNavigate();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const filteredRows = useMemo(() => {
    if (
      hasAccess(accessKeys.freePackage) &&
      hasAccess(accessKeys.courseAdmin) &&
      hasAccess(accessKeys.digitalLicenses)
    ) {
      return rows.filter((r) => !!r.digitalLicenseId);
    }
    return rows;
  }, [rows]);

  const localizedTextsMap = {
    columnMenuUnsort: Translate.get("DoNotSort"),
    columnMenuSortAsc: Translate.get("SortAscending"),
    columnMenuSortDesc: Translate.get("SortDescending"),
    columnMenuFilter: Translate.get("Filter"),
    columnMenuHideColumn: Translate.get("Hide"),
    columnMenuShowColumns: Translate.get("Show"),
    toolbarDensity: Translate.get("RowHeight"),
    toolbarExport: Translate.get("Export"),
    toolbarFilters: Translate.get("Filter"),
    toolbarColumns: Translate.get("Columns"),
    MuiTablePagination: {
      labelRowsPerPage: Translate.get("ShowNumberEntries"),
      labelDisplayedRows: ({ from, to, count }) =>
        `${from}-${to} ${Translate.get("Of")} ${count !== -1 ? count : `${Translate.get("Total")} ${to}`}`,
    },
  };

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  let columns = [];

  function isOrderLicenseAvailable(courseType) {
    // Advanced can print all courseTypes, while standard is limited to
    // the types available in <LicenseRegistration />
    return (
      hasSomeAccess(accessKeys.advancedPackage, accessKeys.standardPackage) ||
      licenseRegistrationCourseTypes.includes(courseType)
    );
  }

  function handleOrderLicensesClick(actorLicenseId) {
    setPrintActorLicenseId(actorLicenseId);
  }

  function handleClosePrintDialog(isPrintDone) {
    setPrintActorLicenseId(null);
    if (isPrintDone) {
      if (onOrderLicenseSuccess) {
        onOrderLicenseSuccess();
      } else {
        navigate(0);
      }
    }
  }

  async function deleteActorLicense(actorLicenseId) {
    const response = await api.fetch(
      `${import.meta.env.VITE_MAIN_URL}actors/actorlicenses/${actorLicenseId}`,
      true,
      "DELETE",
    );
    if (response && response.isSuccessful) {
      const successMessage = enqueueSnackbar(Translate.get("Ready"), {
        variant: "success",
        autoHideDuration: 6000,
        onClick: () => closeSnackbar(successMessage),
      });
      navigate(0);
    }
    setDeleteDetails(null);
  }

  async function requestDigitalLicenseArchival(digitalLicenseId) {
    if (!digitalLicenseId) {
      throw new Error();
    }

    const response = await api.fetch(
      `${import.meta.env.VITE_MAIN_URL}digital/licenses/${digitalLicenseId}/archive/request`,
      true,
      "POST",
    );
    if (response && response.isSuccessful) {
      const successMessage = enqueueSnackbar(Translate.get("Ready"), {
        variant: "success",
        autoHideDuration: 6000,
        onClick: () => closeSnackbar(successMessage),
      });
      navigate(0);
    }
    setDeleteDetails(null);
  }

  async function deleteDigitalLicenseWithoutArchival(digitalLicenseId) {
    if (!digitalLicenseId) {
      throw new Error();
    }

    const response = await api.fetch(
      `${import.meta.env.VITE_MAIN_URL}digital/licenses/${digitalLicenseId}`,
      true,
      "DELETE",
    );
    if (response && response.isSuccessful) {
      const successMessage = enqueueSnackbar(Translate.get("Ready"), {
        variant: "success",
        autoHideDuration: 6000,
        onClick: () => closeSnackbar(successMessage),
      });
      navigate(0);
    }
    setDeleteDetails(null);
  }

  async function resendArciveRequestForDigitalLicense(digitalLicenseId) {
    const response = await api.fetch(
      `${import.meta.env.VITE_MAIN_URL}digital/licenses/${digitalLicenseId}/archive/request/resend`,
      true,
      "POST",
    );
    if (response && response.isSuccessful) {
      const successMessage = enqueueSnackbar(Translate.get("Ready"), {
        variant: "success",
        autoHideDuration: 6000,
        onClick: () => closeSnackbar(successMessage),
      });
      navigate(0);
    }
    setResendArchiveRequestForDigitalLicenseId(null);
  }

  function onResendArchiveRequest(actorLicenseId) {
    const actorLicense = filteredRows.find((r) => r.actorLicenseId === actorLicenseId);
    setResendArchiveRequestForDigitalLicenseId(actorLicense.digitalLicenseId);
  }

  function handleDelete(actorLicenseId, digitalLicenseRegDate) {
    const actorLicense = filteredRows.find((r) => r.actorLicenseId === actorLicenseId);
    if (!actorLicense.digitalLicenseId) {
      setDeleteDetails({
        title: Translate.get("DeleteActorLicense"),
        message: Translate.get("SureDeleteActorLicense"),
        action: async () => await deleteActorLicense(actorLicenseId),
      });
    } else {
      const fourteenDaysAgo = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000);
      const isWithinGracePeriod = !!digitalLicenseRegDate && new Date(digitalLicenseRegDate) >= fourteenDaysAgo;
      const options = isWithinGracePeriod
        ? [
            {
              type: "checkbox",
              text: Translate.get("OptionDeleteDigitalLicenseWithoutArchival"),
              key: "deleteWithoutArchival",
            },
          ]
        : null;
      const action = async ({ deleteWithoutArchival }) =>
        deleteWithoutArchival
          ? await deleteDigitalLicenseWithoutArchival(actorLicense.digitalLicenseId)
          : await requestDigitalLicenseArchival(actorLicense.digitalLicenseId);

      if (actorLicense.isArchiveRequestResponseRequiredForDelete) {
        setDeleteDetails({
          title: Translate.get("DeleteActorLicenseWithDigitalLicense"),
          message: Translate.get("SureDeleteActorLicenseWithDigitalLicense"),
          options,
          action,
        });
      } else {
        setDeleteDetails({
          title: Translate.get("DeleteActorLicenseWithDigitalLicense"),
          message: Translate.get("SureDeleteActorLicenseWithDigitalLicenseExistingResponse"),
          options,
          action,
        });
      }
    }
  }

  if (!smallScreen) {
    columns = [
      {
        field: "licenseGroupDescription",
        headerName: Translate.get("LicenceType"),
        flex: 1,
        renderHeader: () => <IconAndLabelColumnHeader icon={SchoolIcon} label={Translate.get("LicenceType")} />,
      },
      {
        field: "customx",
        headerName: Translate.get("Competences"),
        renderHeader: () => <IconAndLabelColumnHeader icon={BuildIcon} label={Translate.get("Competences")} />,
        renderCell: (params) => {
          const value = [params.row.licenseGroupPractice, params.row.licenseGroupTheory].filter((s) => !!s).join(", ");

          return (
            <div title={value} style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {value}
            </div>
          );
        },
        valueGetter: (value, row, column, apiRef) => row.licenseGroupPractice || "",
        sortComparator: (v1, v2) => v1.localeCompare(v2),
        flex: 1,
      },
      {
        field: "validFrom",
        flex: 1,
        headerName: Translate.get("ValidTime"),
        renderHeader: () => <IconAndLabelColumnHeader icon={CalendarTodayIcon} label={Translate.get("ValidTime")} />,
        valueGetter: (value, row, column, apiRef) =>
          `${row.validFrom ? formatDate(row.validFrom) : ""} - ${row.validTo ? formatDate(row.validTo) : ""}`,
      },
      {
        field: "instructorName",
        headerName: Translate.get("Instructor"),
        flex: 1,
        renderHeader: () => <IconAndLabelColumnHeader icon={CoPresentIcon} label={Translate.get("Instructor")} />,
      },
      {
        field: "lastPrintDate",
        headerName: Translate.get("LastOrderedDateAndNumberOfPrints"),
        flex: 1,
        renderHeader: () => (
          <IconAndLabelColumnHeader
            icon={EventAvailableIcon}
            label={Translate.get("LastOrderedDateAndNumberOfPrints")}
          />
        ),
        valueGetter: (value, row, column, apiRef) =>
          row.lastPrintDate ? `${formatDate(row.lastPrintDate)} (${row.numberOfPrints})` : "",
      },
      ...(hasAccess(accessKeys.digitalLicenses)
        ? [
            {
              field: "customDigitalLicense",
              headerName: Translate.get("DigitalLicenseLabel"),
              flex: 1,
              display: "flex",
              renderHeader: () => (
                <IconAndLabelColumnHeader icon={CloudIcon} label={Translate.get("DigitalLicenseLabel")} />
              ),
              renderCell: (params) => <DigitalLicenseColumn license={params.row} />,
            },
          ]
        : []),
      {
        field: "custom1",
        headerName: Translate.get("PlaceOrder"),
        flex: 1,
        minWidth: 200,
        sortable: false,
        renderHeader: () => (
          <div></div>
          /*  <div className="stdFlexLeft">
            <DomainIcon />
            <div>{Translate.get("PlaceOrder")}</div>
          </div> */
        ),
        renderCell: (params) => {
          return (
            //params.row

            <Button
              variant="outlined"
              disabled={!isOrderLicenseAvailable(params.row.licenseGroupDescription?.trim())}
              onClick={() => handleOrderLicensesClick(params.row.actorLicenseId)}
            >
              {Translate.get("OrderLicense")}
            </Button>
          );
        },
      },
      {
        field: "custom2",
        headerName: "",
        flex: 0,
        align: "right",
        sortable: false,
        maxWidth: 50,
        //hide:hideFilter,

        renderHeader: () => <div></div>,
        renderCell: (params) => {
          const menuActions = getDotMenuActions({
            license: params.row,
            actorOrgNo,
            actorEmail,
            onDeleteLicense: () => handleDelete(params.row.actorLicenseId, params.row.digitalLicenseRegDate),
            onEditDigitalLicense: () => setEditDigitalLicense(params.row),
            onResendArchiveRequest: () => onResendArchiveRequest(params.row.actorLicenseId),
          });
          return <div>{menuActions.length > 0 && <DotMenu content={menuActions} horizontal />}</div>;
        },
      },
    ];
  }

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 5,
    page: 0,
  });

  useEffect(() => {
    async function loadLicenseRegistrationCourseTypes() {
      const courseTypes = await getLicenseRegistrationCourseTypes();
      setLicenseRegistrationCourseTypes(courseTypes);
    }
    loadLicenseRegistrationCourseTypes();
  }, []);

  // For small screens
  const listViewColDef = {
    field: "listColumn",
    renderCell: (params) => (
      <LicensCard
        row={params.row}
        dotMenuActions={getDotMenuActions({
          license: params.row,
          actorOrgNo,
          onDeleteLicense: () => handleDelete(params.row.actorLicenseId, params.row.digitalLicenseRegDate),
          onEditDigitalLicense: () => setEditDigitalLicense(params.row),
          onResendArchiveRequest: () => onResendArchiveRequest(params.row.actorLicenseId),
        })}
        isOrderLicenseAvailable={isOrderLicenseAvailable}
        handleOrderLicensesClick={handleOrderLicensesClick}
      />
    ),
  };

  return (
    <>
      {!!printActorLicenseId && (
        <ActorLicensePrintDialog actorLicenseId={printActorLicenseId} onClose={handleClosePrintDialog} />
      )}
      {!!editDigitalLicense && (
        <EditDigitalLicenseDialogBase
          open
          courseId={editDigitalLicense.courseId}
          digitalLicenseId={editDigitalLicense.digitalLicenseId}
          onClose={() => setEditDigitalLicense(null)}
        />
      )}
      <DataGridPremium
        rows={filteredRows}
        columns={columns}
        disableRowSelectionOnClick
        getRowId={(row) => row.actorLicenseId}
        initialState={{
          sorting: {
            sortModel: [{ field: "validFrom", sort: "desc" }],
          },
        }}
        localeText={localizedTextsMap}
        pagination
        pageSizeOptions={[5]}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        slots={{
          noRowsOverlay: CustomNoRowsOverlay(Translate.get("EmptyHere")),
        }}
        disableColumnMenu
        getRowHeight={() => (isSmallScreen ? "auto" : 65)}
        listView={isSmallScreen}
        listViewColumn={isSmallScreen ? listViewColDef : undefined}
      />
      {deleteDetails && (
        <YesOrNoDialog
          open={!!deleteDetails}
          title={deleteDetails.title}
          text={deleteDetails.message}
          onNo={() => setDeleteDetails(null)}
          onYes={deleteDetails.action}
          options={deleteDetails.options}
          noText={Translate.get("Cancel")}
          yesText={Translate.get("Delete")}
        />
      )}

      {resendArchiveRequestForDigitalLicenseId && (
        <YesOrNoDialog
          open={!!resendArchiveRequestForDigitalLicenseId}
          title={Translate.get("DeleteActorLicenseWithDigitalLicense")}
          text={Translate.get("ResendDigitalLicenseArchiveRequestPrompt")}
          onNo={() => setResendArchiveRequestForDigitalLicenseId(null)}
          onYes={async () => await resendArciveRequestForDigitalLicense(resendArchiveRequestForDigitalLicenseId)}
          noText={Translate.get("Cancel")}
          yesText={Translate.get("SendReminder")}
        />
      )}
    </>
  );
};
export default Comp;
